import React from 'react';
import { YStack, XStack, SizableText, H4, Card } from 'tamagui';
import type { UIComponentContract } from '../../types/componentContract';

export const uiContract: UIComponentContract = {
  id: "analytics.summary",
  accepts: {
    capability: "read_analytics",
    shape: {
      total: "number",
      change: "number?",
      period: "string?",
      metrics: "object?"
    },
    required: ["total"],
    optional: ["change", "period", "metrics"]
  },
  renders: "card",
  priority: 8,
  surface: "dashboard"
};

interface AnalyticsSummaryProps {
  data: {
    total: number;
    change?: number;
    period?: string;
    metrics?: Record<string, any>;
  };
}

export const AnalyticsSummary: React.FC<AnalyticsSummaryProps> = (props) => {
  const { data } = props || {};

  if (!data) {
    return (
      <Card borderRadius={16} padding="$5" elevate backgroundColor="$background">
        <SizableText>No analytics data available</SizableText>
      </Card>
    );
  }

  const changeColor = data.change && data.change >= 0 ? "$green10" : "$red10";
  const changeSign = data.change && data.change >= 0 ? "+" : "";

  return (
    <Card
      borderRadius={16}
      padding="$5"
      elevate
      backgroundColor="$background"
    >
      <YStack gap="$3">
        <H4 fontWeight="700">Analytics Summary</H4>

        <XStack alignItems="baseline" gap="$2">
          <SizableText fontSize="$10" size="$6" fontWeight="bold">
            {data.total.toLocaleString()}
          </SizableText>
          {data.period && (
            <SizableText size="$4" color="$gray10">
              {data.period}
            </SizableText>
          )}
        </XStack>

        {data.change !== undefined && (
          <SizableText size="$4" color={changeColor}>
            {changeSign}{data.change}% change
          </SizableText>
        )}

        {data.metrics && Object.keys(data.metrics).length > 0 && (
          <YStack gap="$2" marginTop="$2">
            {Object.entries(data.metrics).map(([key, value]) => (
              <XStack key={key} justifyContent="space-between">
                <SizableText size="$3" color="$gray10">
                  {key}:
                </SizableText>
                <SizableText size="$3" fontWeight="600">
                  {typeof value === 'number' ? value.toLocaleString() : String(value)}
                </SizableText>
              </XStack>
            ))}
          </YStack>
        )}
      </YStack>
    </Card>
  );
};

// Attach contract to component
(AnalyticsSummary as any).uiContract = uiContract;

export default AnalyticsSummary;

